import React, { useMemo } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import moment from "moment";
import EditableForecastCell from "./EditableForecastCell";

const PINK = "#FCE4EC";
const YELLOW = "#FFF9C4";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    border: "1px solid #e0e0e0",
    borderRadius: 4,
  },
  table: {
    "& .MuiTableCell-stickyHeader": {
      backgroundColor: "#F5F5F5",
      fontWeight: 600,
      color: "#212121",
    },
  },
  tableCell: {
    padding: "10px 14px",
    fontSize: "0.85rem",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #f0f0f0",
  },
  numericCell: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  legendRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    fontSize: "0.85rem",
  },
  legendSwatch: {
    width: 18,
    height: 14,
    border: "1px solid #ccc",
    display: "inline-block",
  },
  pager: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },
  pagerText: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
}));

const monthColumnsFromRange = (start, end) => {
  const list = [];
  let cur = moment(start, "YYYY-MM-DD").startOf("month");
  const last = moment(end, "YYYY-MM-DD").startOf("month");
  while (cur.isSameOrBefore(last, "month")) {
    list.push(cur.format("YYYY-MM-DD"));
    cur = cur.add(1, "month");
  }
  return list;
};

const groupRows = (rows) => {
  const map = new Map();
  rows.forEach((r) => {
    const key = [
      r.cmoOrgID,
      r.sponsorOrgID,
      r.collabrixProductID,
      r.productCode,
    ].join("||");
    if (!map.has(key)) {
      map.set(key, {
        productCode: r.productCode,
        productDescription: r.productDescription,
        uom: r.uom,
        cmoOrgID: r.cmoOrgID,
        sponsorOrgID: r.sponsorOrgID,
        collabrixProductID: r.collabrixProductID,
        collabrixForecastSyncID: r.collabrixForecastSyncID,
        cells: {},
      });
    }
    map.get(key).cells[r.forecastMonth] = r;
  });
  return Array.from(map.values());
};

const cellStyle = (cell) => {
  if (!cell) return {};
  if (cell.exceedsLowLimit) return { backgroundColor: PINK };
  if (cell.exceedsHighLimit) return { backgroundColor: YELLOW };
  return {};
};

const fmtNum = (n) =>
  n == null || n === ""
    ? ""
    : Number(n).toLocaleString("en-US", { maximumFractionDigits: 3 });

export default function ForecastTable({
  forecastList,
  dataLoading,
  editMode,
  editBuffer,
  onCellChange,
  filters,
  pagination,
  goPage,
}) {
  const classes = useStyles();

  const monthCols = useMemo(
    () => monthColumnsFromRange(filters.startMonth, filters.endMonth),
    [filters.startMonth, filters.endMonth]
  );

  const grouped = useMemo(() => groupRows(forecastList || []), [forecastList]);

  const totalPages = pagination.noOfPages || 1;
  const currentPage = Math.min(filters.pageNumber || 1, totalPages);

  return (
    <Paper className={classes.wrapper} variant="outlined">
      {dataLoading ? (
        <Box className={classes.emptyState}>
          <CircularProgress size={28} />
        </Box>
      ) : grouped.length === 0 ? (
        <Box className={classes.emptyState}>
          <Typography variant="body2">
            No forecast data for the selected filters.
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer style={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableCell}>
                    Material Code / Description
                  </TableCell>
                  <TableCell className={classes.tableCell}>UoM</TableCell>
                  {monthCols.map((m) => (
                    <TableCell
                      key={m}
                      className={`${classes.tableCell} ${classes.numericCell}`}
                    >
                      {moment(m, "YYYY-MM-DD").format("MMM-YYYY")}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {grouped.map((g) => (
                  <TableRow key={g.productCode}>
                    <TableCell
                      className={`${classes.tableCell}`}
                    >
                      {g.productCode} / {g.productDescription}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {g.uom}
                    </TableCell>
                    {monthCols.map((m) => {
                      const cell = g.cells[m];
                      const cellKey = `${g.collabrixProductID}__${m}`;
                      const syntheticId = `new__${g.collabrixProductID}__${m}`;
                      return (
                        <TableCell
                          key={m}
                          className={`${classes.tableCell} ${classes.numericCell}`}
                          style={cellStyle(cell)}
                        >
                          {editMode ? (
                            cell ? (
                              <EditableForecastCell
                                row={cell}
                                cellKey={cellKey}
                                editBuffer={editBuffer}
                                onCellChange={onCellChange}
                              />
                            ) : (
                              <EditableForecastCell
                                row={{ collabrixForecastDetailID: syntheticId, quantity: 0 }}
                                editBuffer={editBuffer}
                                onCellChange={(id, qty) =>
                                  onCellChange(id, qty, {
                                    collabrixForecastSyncID: g.collabrixForecastSyncID,
                                    collabrixProductID: g.collabrixProductID,
                                    cmoOrgID: g.cmoOrgID,
                                    sponsorOrgID: g.sponsorOrgID,
                                    forecastMonth: m,
                                  })
                                }
                              />
                            )
                          ) : cell ? (
                            fmtNum(cell.quantity)
                          ) : (
                            ""
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box className={classes.legendRow}>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              Legend:
            </Typography>
            <Box className={classes.legendItem}>
              <span
                className={classes.legendSwatch}
                style={{ backgroundColor: YELLOW }}
              />
              <Typography>
                Outside High Limit
              </Typography>
            </Box>
            <Box className={classes.legendItem}>
              <span
                className={classes.legendSwatch}
                style={{ backgroundColor: PINK }}
              />
              <Typography>
                Outside Low Limit
              </Typography>
            </Box>
          </Box>

          <Box className={classes.pager}>
            <IconButton
              size="small"
              disabled={currentPage <= 1}
              onClick={() => goPage(currentPage - 1)}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography className={classes.pagerText}>
              {currentPage} out of {totalPages} pages
            </Typography>
            <IconButton
              size="small"
              disabled={currentPage >= totalPages}
              onClick={() => goPage(currentPage + 1)}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </>
      )}
    </Paper>
  );
}
